/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, Link } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  isFamilyOwner,
  isFamilyPlan,
  URL_WEB,
  useRequestFamily,
  useRequestSubscriptions,
} from "@packages/sdk";
import type { HallowElementProps, ThemeType } from "@packages/ui/emotion";
import {
  Button,
  cssMediaQueryMinWidthTablet,
  DesktopDownloadCTA,
  MobileDownloadCTA,
  radius,
  Skeleton,
  spacing,
  stroke,
  Text,
  Toast,
  useCopyToClipboard,
} from "@packages/ui/emotion";
import { CopyIcon } from "@packages/ui/src/components/_base/icons/Icon/GeneralIcons";
import { HallowDataTestId } from "@tools/playwright/global";
import { useState } from "react";

const cssMinWidth976 = "@media screen and (min-width: 976px)";

const URL_APP_STORE_WITH_TRACKING = "https://hallow.app.link/VEaQyWbWAQb";

const divStyle: Interpolation = {
  alignItems: "center",
  display: "none",
  flexDirection: "column",
  gap: spacing.xxl,
  justifyContent: "center",
  paddingBottom: spacing.xxl,
  paddingLeft: spacing.xxl,
  paddingRight: spacing.xxl,
  paddingTop: 80,
  width: "100%",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
  [cssMinWidth976]: {
    flexDirection: "row",
    gap: 80,
  },
};
const downloadContainerStyle: Interpolation = {
  display: "flex",
  gap: spacing.l,
};
const downloadTextContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
};
const h2Style: Interpolation = {
  textAlign: "center",
  [cssMinWidth976]: { textAlign: "left" },
};
const imgStyle: Interpolation = {
  maxHeight: 400,
};
const pStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});
const qrStyle: Interpolation = {
  height: 150,
  width: 150,
};
const textContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.l,
  maxWidth: 600,
  minWidth: 400,
};
const inviteBoxStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  background: theme.semanticColors.neutralsLowest,
  borderRadius: radius.l,
  display: "none",
  gap: spacing.m,
  justifyContent: "space-between",
  padding: spacing.l,
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
});
const inviteBoxTextStyle: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  justifyContent: "flex-start",
};
const inviteBoxExplanationStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});

const divMobileStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  paddingBottom: spacing.xl,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.xl,
  [cssMediaQueryMinWidthTablet]: { display: "none" },
};
const downloadButtonsContainerMobileStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  width: "100%",
};
const downloadTextContainerMobileStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  textAlign: "center",
};
const imgContainerMobileStyle: Interpolation = {
  display: "flex",
  justifyContent: "center",
  maxWidth: 375,
  minWidth: 235,
  width: "100%",
};
const imgMobileStyle: Interpolation = {
  maxHeight: 235,
};
const pMobileStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});
const inviteBoxMobileStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.l,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  justifyContent: "flex-start",
  padding: spacing.l,
  width: spacing.full,
  [cssMediaQueryMinWidthTablet]: { display: "none" },
});
const inviteBoxTextMobileStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  textAlign: "center",
};
const copyMobileStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.accentMedium,
});
const inviteSkeleton: Interpolation = {
  height: spacing.xxl,
};

type DownloadBannerProps = Omit<HallowElementProps<"div">, "children">;

export const DownloadBanner = (props: DownloadBannerProps) => {
  const { query: subscriptionsQuery } = useRequestSubscriptions();
  const { track } = useAnalytics();

  const handleDownload = ({ platform }: { platform: "apple" | "google" }) => {
    track({
      event: HallowAnalyticsEvent.TappedBannerDownload,
      properties: {
        option: "download",
        platform,
      },
    });
  };

  const showFamilyInviteBox =
    (subscriptionsQuery.data &&
      isFamilyPlan("subscription", subscriptionsQuery.data?.[0]) &&
      isFamilyOwner(subscriptionsQuery.data?.[0])) ||
    false;

  return (
    <>
      <div css={divStyle} {...props}>
        <div css={textContainerStyle}>
          <Text
            as="h2"
            size="l"
            type="display"
            css={h2Style}
            data-testid={HallowDataTestId.DownloadBannerHeaderDesktop}
          >
            <I18nMessage message="download_banner_header" />
          </Text>
          <div css={downloadContainerStyle}>
            <img alt="" src="/assets/qr/qr-home.webp" css={qrStyle} />
            <div css={downloadTextContainerStyle}>
              <Text as="p" size="l" type="body" css={pStyle}>
                <I18nMessage message="download_banner_description" />
              </Text>
              <DesktopDownloadCTA handleDownload={handleDownload} />
            </div>
          </div>
          {showFamilyInviteBox && (
            <div css={inviteBoxStyle}>
              <div css={inviteBoxTextStyle}>
                <Text as="h3" type="title" size="l">
                  <I18nMessage message="web_family_plan_invite_header" />
                </Text>
                <Text css={inviteBoxExplanationStyle}>
                  <I18nMessage message="manage_family_share_description" />
                </Text>
              </div>
              <Button size="l" asChild>
                <Link href="/settings/family">
                  <I18nMessage message="manage_family_invite_member" />
                </Link>
              </Button>
            </div>
          )}
        </div>
        <img alt="" src="/assets/angled-phones.webp" css={imgStyle} />
      </div>
      <div css={divMobileStyle} {...props}>
        <div css={imgContainerMobileStyle}>
          <img alt="" src="/assets/angled-phones.webp" css={imgMobileStyle} />
        </div>
        <div css={downloadTextContainerMobileStyle}>
          <Text
            as="h2"
            size="m"
            type="headline"
            data-testid={HallowDataTestId.DownloadBannerHeaderMobile}
          >
            <I18nMessage message="download_banner_header" />
          </Text>
          <Text as="p" size="m" css={pMobileStyle} type="body">
            <I18nMessage message="download_popup_description" />
          </Text>
        </div>
        <div css={downloadButtonsContainerMobileStyle}>
          <MobileDownloadCTA
            handleDownload={handleDownload}
            appStoreOverride={URL_APP_STORE_WITH_TRACKING}
          />
        </div>
        {showFamilyInviteBox && (
          <div css={inviteBoxMobileStyle}>
            <div css={inviteBoxTextMobileStyle}>
              <Text as="h3" type="title" size="l">
                <I18nMessage message="web_family_plan_invite_header" />
              </Text>
              <Text css={inviteBoxExplanationStyle}>
                <I18nMessage message="manage_family_share_description" />
              </Text>
            </div>
            <CopyFamilyInviteButton />
          </div>
        )}
      </div>
    </>
  );
};

const CopyFamilyInviteButton = () => {
  const { query } = useRequestFamily();
  const [, copyToClipboard] = useCopyToClipboard();
  const url = query.data
    ? `${URL_WEB}/invite/${query.data?.invite_code}/`
    : null;
  const [showToast, setShowToast] = useState<boolean>(false);

  const handleClick = () => {
    copyToClipboard(url ?? "");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2750);
  };

  if (query.data?.invite_code) {
    return (
      <>
        <Button
          variant="neutral"
          endIcon={<CopyIcon />}
          isFullWidth
          css={copyMobileStyle}
          onClick={handleClick}
        >
          <>
            <Text overflow="ellipsis" type="button">
              {url}
            </Text>
          </>
        </Button>
        {showToast && (
          <Toast>
            <I18nMessage message="copied_to_clip_confirmation_toast" />
          </Toast>
        )}
      </>
    );
  }
  return <Skeleton css={inviteSkeleton} />;
};
